(() => {
  'use strict';

  const browserWindow = typeof window !== 'undefined' ? window : null;
  const allowedImageTypes = new Set(['image/jpeg', 'image/png', 'image/webp']);
  const maxImageBytes = 10 * 1024 * 1024;

  const createRequestId = () => {
    const cryptoObject = typeof globalThis !== 'undefined' ? globalThis.crypto : null;
    if (cryptoObject && typeof cryptoObject.randomUUID === 'function') return cryptoObject.randomUUID();
    if (cryptoObject && typeof cryptoObject.getRandomValues === 'function' && typeof Uint8Array !== 'undefined') {
      const bytes = new Uint8Array(16);
      cryptoObject.getRandomValues(bytes);
      bytes[6] = (bytes[6] & 15) | 64;
      bytes[8] = (bytes[8] & 63) | 128;
      const hex = [...bytes].map((byte) => byte.toString(16).padStart(2, '0')).join('');
      return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
    }
    return `tis-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
  };

  const sameIdentifier = (left, right) => left !== undefined && left !== null && right !== undefined && right !== null && String(left) === String(right);

  const eventBelongsToRoot = (event, form, currentVariantId) => {
    if (!event || event.target !== form) return false;
    const detail = event.detail;
    const eventVariantId = detail && typeof detail === 'object' ? detail.variantId : '';
    return !eventVariantId || sameIdentifier(eventVariantId, currentVariantId);
  };

  const initializeForm = (root) => {
    if (!root || root.dataset?.tisPersonalizationInitialized === 'true') return root?.tisPersonalizationController || null;

    const form = root.closest?.('form') || root.form || root.querySelector?.('form') || null;
    const toggle = root.querySelector?.('[data-personalization-toggle]') || null;
    const fieldset = root.querySelector?.('[data-personalization-fields]') || null;
    const variantInput = root.querySelector?.('[data-personalization-variant-input]') || form?.querySelector?.('input[name="id"]') || root.querySelector?.('input[name="id"]') || null;
    const requestInput = root.querySelector?.('[data-personalization-request-id]') || null;
    const personalizationProperties = [...(root.querySelectorAll?.('[data-personalization-property]') || [])];
    const acceleratedCheckout = form?.querySelector?.('[data-accelerated-checkout]') || root.querySelector?.('[data-accelerated-checkout]') || null;
    const imageInputs = [...(root.querySelectorAll?.('[data-personalization-image]') || [])];
    const fileError = root.querySelector?.('[data-personalization-file-error]') || null;
    const caption = root.querySelector?.('[data-personalization-caption]') || null;
    const briefDisclosure = root.querySelector?.('[data-personalization-brief]') || null;
    const editorial = form?.closest?.('[data-product-editorial]') || root.closest?.('[data-product-editorial]') || null;
    const editorialCopyStates = editorial
      ? [...(editorial.querySelectorAll?.('[data-personalization-diy-copy], [data-personalization-shop-copy]') || [])].map((element) => ({
        element,
        kind: element.hasAttribute?.('data-personalization-diy-copy') ? 'diy' : 'shop',
        initialHidden: Boolean(element.hidden),
      }))
      : [];
    const sourceVariantId = root.dataset?.sourceVariantId || variantInput?.dataset?.sourceVariantId || '';
    const personalizationVariantId = root.dataset?.personalizationVariantId || variantInput?.dataset?.personalizationVariantId || '';
    const initialAcceleratedHidden = Boolean(acceleratedCheckout?.hidden);
    const initialAcceleratedAriaHidden = acceleratedCheckout?.getAttribute?.('aria-hidden');
    let hasSuccessfulAdd = root.dataset?.personalizationSubmitted === 'true';
    let changedSinceSuccessfulAdd = false;
    const invalidImageEvents = new WeakSet();

    if (root.dataset) root.dataset.tisPersonalizationInitialized = 'true';

    const setRequestId = () => {
      const requestId = createRequestId();
      if (requestInput) requestInput.value = requestId;
      if (root.dataset) root.dataset.personalizationRequestId = requestId;
      return requestId;
    };

    if (requestInput && !requestInput.value) setRequestId();
    const currentRequestId = () => requestInput?.value || root.dataset?.personalizationRequestId || '';

    const setAcceleratedCheckout = (active) => {
      if (!acceleratedCheckout) return;
      acceleratedCheckout.hidden = active ? true : initialAcceleratedHidden;
      if (active) {
        acceleratedCheckout.setAttribute?.('aria-hidden', 'true');
      } else if (initialAcceleratedAriaHidden === null || initialAcceleratedAriaHidden === undefined) {
        acceleratedCheckout.removeAttribute?.('aria-hidden');
      } else {
        acceleratedCheckout.setAttribute?.('aria-hidden', initialAcceleratedAriaHidden);
      }
    };

    const setEditorialCopy = (active) => {
      editorialCopyStates.forEach(({ element, kind, initialHidden }) => {
        element.hidden = active ? kind === 'diy' : initialHidden;
      });
    };

    const showFileError = (message) => {
      if (!fileError) return;
      fileError.textContent = message;
      fileError.hidden = !message;
      if (message && briefDisclosure) briefDisclosure.open = true;
    };

    const revealInvalidField = (event) => {
      const target = event?.target;
      if (!target?.matches?.('[data-personalization-field]') || target.disabled) return;
      let ancestor = target.parentElement;
      while (ancestor && ancestor !== root) {
        if (ancestor.matches?.('details')) ancestor.open = true;
        ancestor = ancestor.parentElement;
      }
    };

    const validateImageSelection = (event) => {
      const invalidInputs = imageInputs.filter((input) => {
        const file = input.files?.[0];
        return Boolean(file && (!allowedImageTypes.has(file.type) || file.size > maxImageBytes));
      });

      if (event && invalidInputs.includes(event.target)) invalidImageEvents.add(event);

      invalidInputs.forEach((input) => {
        try {
          input.value = '';
        } catch (error) {
          // A non-browser test double may expose a read-only file value.
        }
      });

      showFileError(invalidInputs.length ? 'Use a JPG, PNG, or WebP image up to 10 MB.' : '');
      return invalidInputs.length === 0;
    };

    const setActive = (active) => {
      const nextActive = Boolean(active && personalizationVariantId);
      if (toggle) toggle.checked = nextActive;
      if (fieldset) fieldset.disabled = !nextActive;
      personalizationProperties.forEach((property) => { property.disabled = !nextActive; });
      if (variantInput) variantInput.value = nextActive ? personalizationVariantId : sourceVariantId;
      if (root.dataset) root.dataset.personalizationActive = String(nextActive);
      if (form?.dataset) form.dataset.personalizationActive = String(nextActive);
      setAcceleratedCheckout(nextActive);
      setEditorialCopy(nextActive);
      if (caption) {
        caption.hidden = !nextActive;
        if (nextActive) caption.removeAttribute?.('aria-hidden');
        else caption.setAttribute?.('aria-hidden', 'true');
      }
      if (toggle) {
        const descriptions = (toggle.getAttribute('aria-describedby') || '')
          .split(/\s+/)
          .filter((id) => id && id !== caption?.id);
        if (nextActive && caption?.id) descriptions.push(caption.id);
        toggle.setAttribute('aria-describedby', descriptions.join(' '));
      }
      return nextActive;
    };

    const rotateAfterBriefChange = (event) => {
      const target = event?.target;
      if (!target?.matches?.('[data-personalization-field]') || target.disabled || invalidImageEvents.has(event)) return;
      if (event.type === 'input' && target.type === 'file') return;
      if (!hasSuccessfulAdd || changedSinceSuccessfulAdd) return;
      setRequestId();
      changedSinceSuccessfulAdd = true;
      if (root.dataset) root.dataset.personalizationSubmitted = 'false';
    };

    const markCartAdded = (event) => {
      if (!eventBelongsToRoot(event, form, variantInput?.value || '')) return;
      hasSuccessfulAdd = true;
      changedSinceSuccessfulAdd = false;
      if (root.dataset) root.dataset.personalizationSubmitted = 'true';
    };

    toggle?.addEventListener('change', () => setActive(toggle.checked));
    imageInputs.forEach((input) => input.addEventListener('change', validateImageSelection));
    root.addEventListener?.('input', rotateAfterBriefChange);
    root.addEventListener?.('change', rotateAfterBriefChange);
    form?.addEventListener?.('invalid', revealInvalidField, true);
    form?.addEventListener?.('tis:cart-added', markCartAdded);

    setActive(Boolean(toggle?.checked));
    validateImageSelection();
    if (root.dataset) root.dataset.personalizationReady = 'true';

    const controller = {
      setActive,
      setRequestId,
      markCartAdded,
      getRequestId: currentRequestId,
    };
    if (root) root.tisPersonalizationController = controller;
    return controller;
  };

  const initialize = (scope) => {
    const target = scope || browserWindow?.document;
    if (!target) return [];
    const roots = [];
    if (target.matches?.('[data-personalization-form]')) roots.push(target);
    target.querySelectorAll?.('[data-personalization-form]').forEach((root) => roots.push(root));
    return [...new Set(roots)].map(initializeForm).filter(Boolean);
  };

  const api = { createRequestId, initialize, initializeForm };
  if (browserWindow) {
    browserWindow.TISPersonalization = api;
    if (browserWindow.document) {
      const start = () => initialize(browserWindow.document);
      if (browserWindow.document.readyState === 'loading') {
        browserWindow.document.addEventListener('DOMContentLoaded', start, { once: true });
      } else {
        start();
      }
      browserWindow.document.addEventListener('shopify:section:load', (event) => initialize(event.target));
    }
  }
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
})();
